/**
 * Lecture des relevés bancaires CODA (fichiers `.BC2` chez ING).
 *
 * CODA est le format interbancaire belge normalisé par Febelfin. Il remplace
 * avantageusement l'export CSV pour trois raisons vérifiées sur des fichiers
 * réels :
 *
 * - la communication structurée y a un **champ dédié**, alors que le CSV oblige
 *   à la deviner par expression régulière dans du texte destiné à des humains ;
 * - l'enregistrement de fin porte des **totaux de contrôle** : un import peut se
 *   vérifier lui-même et refuser un fichier incohérent ;
 * - le **code opération** distingue un virement entrant d'un paiement par carte
 *   ou d'un prélèvement de frais bancaires.
 *
 * Deux pièges d'encodage, constatés sur les fichiers de production :
 * les lignes sont terminées en CRLF (129 caractères au lieu de 128) et le texte
 * est en CP1252, pas en UTF-8.
 *
 * ## Les enregistrements utiles
 *
 * | Type | Contenu | Ce qu'on en tire |
 * |---|---|---|
 * | `21` | le mouvement : montant, date, code opération, communication | tout le cœur |
 * | `23` | **le compte et le nom de la contrepartie** | l'IBAN de l'émetteur |
 * | `31`, `32` | texte libre et adresse, en continuation | le détail pour l'œil humain |
 *
 * L'enregistrement `23` est celui qui rend possible le rapprochement manuel
 * quand la communication ne dit rien : on remonte de l'IBAN à la famille. Sur
 * 802 mouvements des relevés réels, 785 en portent un.
 *
 * Les positions ci-dessous ont été validées sur 22 fichiers, 800 mouvements.
 */

import { parseCommunication, recoverFromFreeText, type ParsedCommunication } from './communication';

/** Longueur utile d'un enregistrement, hors fin de ligne. */
export const CODA_RECORD_LENGTH = 128;

/**
 * Familles d'opération à retenir comme mouvements de compte à compte.
 *
 * Un code opération CODA se lit : 1 caractère de type, 2 de famille, 2
 * d'opération, 3 de catégorie. La famille `01` couvre les virements SEPA et
 * `02` les virements instantanés.
 *
 * Tout le reste est du bruit pour le rapprochement : `35` sont les relevés de
 * frais ING, `04` les opérations par carte (paiement Bancontact ou Mastercard,
 * retrait d'espèces). L'ancien importeur ne filtrait pas et avait avalé 232
 * débits qu'il tentait ensuite de rapprocher avec des factures.
 *
 * Du bruit pour le rapprochement, pas pour la comptabilité : ils sont écrits,
 * classés hors périmètre, jamais rapprochés (`nonTransfers`).
 */
export const TRANSFER_FAMILIES = ['01', '02'] as const;

export type MovementDirection = 'credit' | 'debit';

export interface CodaMovement {
  /** Numéro de séquence du mouvement dans le relevé. */
  sequence: string;
  /** Référence bancaire — sert de clé d'idempotence à l'import. */
  reference: string;
  direction: MovementDirection;
  /** Montant en centimes, toujours positif. Le sens est porté par `direction`. */
  amountCents: number;
  /** Date valeur, au format ISO `AAAA-MM-JJ`. */
  valueDate: string;
  /**
   * Date comptable de la banque (« date d'inscription » du CODA, positions
   * 116 à 121 de l'enregistrement 21 ; « Date comptable » de l'export CSV),
   * au format ISO. Absente quand le fichier ne la porte pas.
   *
   * C'est elle qui range le mouvement dans un extrait, donc dans un solde de
   * fin d'année. Cas réel : trois frais ING valeur 31/12/2025 et 31/03/2025,
   * comptabilisés par la banque les 01/01/2026 et 30/01/2026 — absents du
   * solde ING au 31/12/2025, présents dans l'extrait 2026. L'exercice du
   * mouvement se choisit sur elle (voir la migration
   * `la_date_comptable_des_mouvements`).
   */
  entryDate?: string;
  /** Code opération complet sur 8 caractères. */
  transactionCode: string;
  /** Famille d'opération : caractères 2 et 3 du code. */
  family: string;
  /** Vrai pour un virement de compte à compte, faux pour frais et cartes. */
  isTransfer: boolean;
  communication: ParsedCommunication;
  /** Texte libre reconstitué depuis les enregistrements 31 et 32. */
  freeText: string;
  /**
   * IBAN de la contrepartie, lu dans l'enregistrement 23.
   *
   * C'est la seule piste quand la communication ne porte aucune référence — et
   * ces paiements-là pèsent près de la moitié des montants reçus. Un IBAN déjà
   * rattaché à une famille permet de proposer le rapprochement suivant tout
   * seul.
   */
  counterpartyIban?: string;
  /** Nom de la contrepartie, lu dans l'enregistrement 23. Tronqué par la banque. */
  counterpartyName?: string;
  /**
   * Le champ communication de l'enregistrement 21, quand la référence a
   * finalement été retrouvée dans le texte libre. Conservé pour que rien de ce
   * que la banque a transmis ne soit perdu.
   */
  rawCommunicationField?: string;
}

export interface CodaTotals {
  /** Nombre d'enregistrements annoncé, en-tête et pied exclus. */
  recordCount: number;
  debitCents: number;
  creditCents: number;
}

export interface CodaStatement {
  accountIban: string;
  accountName: string;
  statementNumber: string;
  oldBalanceCents: number;
  newBalanceCents: number;
  movements: CodaMovement[];
  totals: CodaTotals;
  /** Écarts entre les totaux annoncés et les totaux recalculés. */
  problems: string[];
}

/** Découpe un fichier en lignes, en absorbant CRLF comme LF. */
function toLines(input: string): string[] {
  return input.split(/\r?\n/).filter((line) => line.trim().length > 0);
}

/**
 * Décode un fichier CODA. Accepte des octets bruts — décodés en CP1252, ce qui
 * est l'encodage réel des fichiers ING — ou une chaîne déjà décodée.
 */
export function decodeCodaBytes(bytes: Uint8Array): string {
  return new TextDecoder('windows-1252').decode(bytes);
}

/** Montant CODA : 15 chiffres exprimant des millièmes d'euro. */
function parseAmountCents(field: string): { cents: number; exact: boolean } {
  const millis = Number(field);
  if (!Number.isFinite(millis)) {
    throw new Error(`montant illisible : ${JSON.stringify(field)}`);
  }
  // Les montants en euros n'ont que deux décimales, donc le millième est
  // toujours nul. On le vérifie plutôt que d'arrondir en silence.
  return { cents: Math.round(millis / 10), exact: millis % 10 === 0 };
}

/** Date CODA `JJMMAA` vers ISO `AAAA-MM-JJ`. */
function parseDate(field: string): string {
  const [dd, mm, yy] = [field.slice(0, 2), field.slice(2, 4), field.slice(4, 6)];
  return `20${yy}-${mm}-${dd}`;
}

/**
 * Une date CODA facultative : `undefined` si le champ est vide, nul ou ne
 * désigne pas un jour du calendrier. Jamais devinée.
 */
function parseOptionalDate(field: string): string | undefined {
  if (!/^\d{6}$/.test(field) || field === '000000') return undefined;
  const iso = parseDate(field);
  const d = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso ? iso : undefined;
}

export function parseCoda(input: string | Uint8Array): CodaStatement {
  const text = typeof input === 'string' ? input : decodeCodaBytes(input);
  const lines = toLines(text);
  const problems: string[] = [];

  if (lines.length === 0) {
    throw new Error('fichier CODA vide');
  }

  const oldBalance = lines.find((l) => l[0] === '1');
  const newBalance = lines.find((l) => l[0] === '8');
  const trailer = lines.find((l) => l[0] === '9');

  if (!oldBalance || !newBalance || !trailer) {
    throw new Error('fichier CODA incomplet : ancien solde, nouveau solde ou pied manquant');
  }

  const accountIban = oldBalance.slice(5, 42).trim();
  const accountName = oldBalance.slice(64, 90).trim();
  const statementNumber = oldBalance.slice(2, 5).trim();

  const movements: CodaMovement[] = [];
  let current: CodaMovement | null = null;

  for (const line of lines) {
    const type = line.slice(0, 2);

    // Un mouvement principal porte le numéro de détail `0000`. Les relevés de
    // frais bancaires ajoutent des enregistrements 21 de détail (0001, 0002…)
    // qui décomposent la TVA : ce ne sont pas des mouvements distincts.
    if (type === '21' && line.slice(6, 10) === '0000') {
      const amount = parseAmountCents(line.slice(32, 47));
      if (!amount.exact) {
        problems.push(`mouvement ${line.slice(2, 6)} : montant avec un millième non nul`);
      }

      const transactionCode = line.slice(53, 61);
      const family = transactionCode.slice(1, 3);
      const isStructured = line[61] === '1';
      const structuredType = isStructured ? line.slice(62, 65) : undefined;
      const commField = isStructured ? line.slice(65, 77) : line.slice(62, 115);

      current = {
        sequence: line.slice(2, 6),
        reference: line.slice(10, 31).trim(),
        direction: line[31] === '1' ? 'debit' : 'credit',
        amountCents: amount.cents,
        valueDate: parseDate(line.slice(47, 53)),
        entryDate: parseOptionalDate(line.slice(115, 121)),
        transactionCode,
        family,
        isTransfer: (TRANSFER_FAMILIES as readonly string[]).includes(family),
        communication: parseCommunication(commField, structuredType),
        freeText: '',
      };
      movements.push(current);
      continue;
    }

    /*
      L'enregistrement 23 porte le compte et le nom de la contrepartie.
      Positions relevées sur les 785 enregistrements des fichiers réels :
      la zone du compte commence en 10, la devise est en [44:47], le nom en [47:82].

      ## Le compte ne fait pas 16 caractères

      Il les faisait tant qu'on ne regardait que des comptes belges : `BE` + 2
      chiffres de clé + 12 de numéro, et pas un de plus. La zone, elle, est plus
      large, et les comptes étrangers l'occupent — 25 caractères au Portugal,
      27 en France et en Italie, 22 en Allemagne.

      Couper à 16 les tronquait donc en silence. Mesuré le 18 septembre 2026 :
      **81 paiements** de onze pays portaient un IBAN amputé, et l'import de
      l'export CSV, qui les connaît en entier, ne les a pas reconnus — **86
      entrées écrites en double, 6 809,40 €**. Un IBAN tronqué n'est pas
      seulement faux : il empêche de retrouver la famille qui a payé.

      On lit donc toute la zone et on s'arrête au premier blanc. Un IBAN n'a ni
      espace ni séparateur, et aucun n'excède 34 caractères — la borne haute de
      la norme.
    */
    if (type === '23' && current) {
      const iban = (line.slice(10, 44).trim().split(/\s/)[0] ?? '').slice(0, 34);
      const nom = line.slice(47, 82).trim();
      // Huit enregistrements sur 785 ont un IBAN vide : on ne fabrique pas un
      // champ vide, on laisse la propriété absente.
      if (iban) current.counterpartyIban = iban;
      if (nom) current.counterpartyName = nom;
      continue;
    }

    // Les enregistrements 31 et 32 portent le texte libre : nom et adresse du
    // donneur d'ordre, et la communication répétée en clair.
    if ((type === '31' || type === '32') && current) {
      current.freeText = (current.freeText + line.slice(40, 113)).trimEnd();
    }
  }

  // Rattrapage par le texte libre.
  //
  // Il ne peut se faire qu'ICI, une fois la boucle terminée : le texte libre est
  // réparti sur les enregistrements 31 et 32 qui SUIVENT le mouvement, il n'est
  // donc pas encore connu au moment où le mouvement est construit.
  //
  // La communication d'origine est conservée dans `communication.raw` d'un
  // mouvement non rattrapé ; pour un mouvement rattrapé, `raw` porte le
  // fragment de texte libre et `recoveredFromFreeText` le signale. Le champ
  // brut de l'enregistrement 21 reste lisible dans `rawCommunicationField`.
  for (const m of movements) {
    if (m.communication.kind !== 'unstructured') continue;
    const rattrape = recoverFromFreeText(m.freeText);
    if (rattrape) {
      m.rawCommunicationField = m.communication.raw;
      m.communication = rattrape;
    }
  }

  // Totaux de contrôle. Le pied compte les enregistrements sans l'en-tête ni
  // lui-même, d'où l'écart systématique de 2 avec le nombre de lignes.
  // Offsets fixes : le pied est `9`, quinze espaces, puis trois champs numériques.
  // Se fier à un `trim()` marcherait par accident — la ligne se termine par un
  // indicateur non nul après les espaces de remplissage.
  const totals: CodaTotals = {
    recordCount: Number(trailer.slice(16, 22)),
    debitCents: Math.round(Number(trailer.slice(22, 37)) / 10),
    creditCents: Math.round(Number(trailer.slice(37, 52)) / 10),
  };

  let debit = 0;
  let credit = 0;
  for (const m of movements) {
    if (m.direction === 'debit') debit += m.amountCents;
    else credit += m.amountCents;
  }
  if (debit !== totals.debitCents) {
    problems.push(`total débit : ${debit} calculé contre ${totals.debitCents} annoncé`);
  }
  if (credit !== totals.creditCents) {
    problems.push(`total crédit : ${credit} calculé contre ${totals.creditCents} annoncé`);
  }
  if (lines.length - 2 !== totals.recordCount) {
    problems.push(
      `nombre d'enregistrements : ${lines.length - 2} calculé contre ${totals.recordCount} annoncé`,
    );
  }

  /*
    Les soldes, avec leur signe : `0` créditeur, `1` débiteur, juste avant le
    montant (position 42 de l'enregistrement 1, 41 de l'enregistrement 8). Lus
    sans lui, un compte à découvert serait lu positif.

    Et le contrôle que les totaux du pied ne font pas : l'ancien solde plus les
    mouvements lus donne le nouveau solde. C'est la preuve que le relevé est lu
    en entier — un mouvement perdu à la lecture la ferait tomber.
  */
  const solde = (ligne: string, signe: number) =>
    (ligne[signe] === '1' ? -1 : 1) * Math.round(Number(ligne.slice(signe + 1, signe + 16)) / 10);
  const oldBalanceCents = solde(oldBalance, 42);
  const newBalanceCents = solde(newBalance, 41);
  if (oldBalanceCents + credit - debit !== newBalanceCents) {
    problems.push(
      `solde : ancien ${oldBalanceCents} + crédits ${credit} − débits ${debit} = `
      + `${oldBalanceCents + credit - debit}, le relevé annonce ${newBalanceCents}`,
    );
  }

  return {
    accountIban,
    accountName,
    statementNumber,
    oldBalanceCents,
    newBalanceCents,
    movements,
    totals,
    problems,
  };
}

/** Les mouvements exploitables pour le rapprochement : virements uniquement. */
export function transfersOnly(statement: CodaStatement): CodaMovement[] {
  return statement.movements.filter((m) => m.isTransfer);
}

/** Paiements reçus. */
export function incomingPayments(statement: CodaStatement): CodaMovement[] {
  return transfersOnly(statement).filter((m) => m.direction === 'credit');
}

/** Virements émis — dont les remboursements aux familles. */
export function outgoingTransfers(statement: CodaStatement): CodaMovement[] {
  return transfersOnly(statement).filter((m) => m.direction === 'debit');
}

/**
 * Tout ce qui n'est pas un virement : cartes, frais, domiciliations, dépôts…
 *
 * Hors du rapprochement, mais pas hors du relevé : chacun est un mouvement du
 * compte, donc une écriture de banque. Jusqu'au 29 septembre 2026, l'import
 * les laissait tomber sans rien dire — invisible tant que l'export CSV, qui
 * les porte, couvrait la période ; après le 17/09, **8 opérations et 317,33 €**
 * manquaient au solde ING. Voir `planLesOperationsHorsVirement`.
 */
export function nonTransfers(statement: CodaStatement): CodaMovement[] {
  return statement.movements.filter((m) => !m.isTransfer);
}
